import { chmod, mkdir, stat, writeFile } from 'node:fs/promises';
import { dirname, join, resolve } from 'node:path';
import { Service, ServiceLifetime } from '@gitlab/needle';
import { getDuoConfigDir } from '@gitlab-org/ai-configuration';
import { formatSessionExport, type SessionExportBundle } from '@gitlab-lsp/workflow-api';
import { UsageError } from '../../utils/usage_error';

// A bundle is redacted but not sanitised, so it stays owner-only.
const FILE_MODE = 0o600;
const DIR_MODE = 0o700;

// Under the Duo config dir so the default path is inside getTrustedReadableDirectories().
const EXPORTS_DIR_NAME = 'exports';

// Colons are illegal in Windows file names; same shape as cli_file_log_writer.ts.
const fileTimestamp = (exportedAt: Date): string =>
  exportedAt.toISOString().replace(/[:.]/g, '-').slice(0, 19);

// Disk only: `/export` shares this and Ink owns stdout in the TUI.
@Service({ dependencies: [], lifetime: ServiceLifetime.Singleton })
export class SessionExportWriter {
  /** Returns the absolute path written. */
  async write(
    bundle: SessionExportBundle,
    { cwd, output }: { cwd: string; output?: string },
  ): Promise<string> {
    const path = output
      ? resolve(cwd, output)
      : this.defaultPathFor(bundle.session.id, new Date(bundle.exportedAt));

    await mkdir(dirname(path), { recursive: true, mode: DIR_MODE });
    await writeFile(path, formatSessionExport(bundle), {
      encoding: 'utf8',
      mode: FILE_MODE,
    });
    await this.#restrictToOwner(path);

    return path;
  }

  // `writeFile`'s mode applies only on create, so an existing `-o` target keeps
  // its own permissions. Skips non-regular files, where chmod fails: `-o
  // /dev/stdout` writes fine today and must keep working.
  async #restrictToOwner(path: string): Promise<void> {
    const stats = await stat(path);
    // eslint-disable-next-line no-bitwise
    if (stats.isFile() && stats.mode & 0o077) {
      await chmod(path, FILE_MODE);
    }
  }

  defaultPathFor(sessionId: string, exportedAt: Date): string {
    const baseDir = process.env.GLAB_CONFIG_DIR || getDuoConfigDir();
    if (!baseDir) {
      throw new UsageError(
        'Could not determine the GitLab Duo configuration directory to export into. Pass --output <path> to choose one.',
      );
    }

    return join(
      baseDir,
      EXPORTS_DIR_NAME,
      `duo-session-${sessionId}-${fileTimestamp(exportedAt)}.json`,
    );
  }
}
